var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BigNumber } from "bignumber.js";
import { NotEnoughBalance, RecipientRequired, InvalidAddress, InvalidAddressBecauseDestinationIsAlsoSource, AmountRequired, NotEnoughBalanceBecauseDestinationNotCreated, FeeNotLoaded, } from "@ledgerhq/errors";
import { formatCurrencyUnit } from "@ledgerhq/coin-framework/currencies/index";
import { PolkadotUnauthorizedOperation, PolkadotElectionClosed, PolkadotNotValidator, PolkadotNoUnlockedBalance, PolkadotNoNominations, PolkadotAllFundsWarning, PolkadotBondMinimumAmount, PolkadotBondMinimumAmountWarning, PolkadotMaxUnbonding, PolkadotValidatorsRequired, PolkadotDoMaxSendInstead, } from "./errors";
import { EXISTENTIAL_DEPOSIT, FEES_SAFETY_BUFFER, isFirstBond, isController, isStash, hasLockedBalance, hasMaxUnlockings, calculateAmount, getMinimumAmountToBond, getMinimumBalance, EXISTENTIAL_DEPOSIT_RECOMMENDED_MARGIN, } from "./logic";
import { isValidAddress } from "./address";
import { getCurrentPolkadotPreloadData } from "./preload";
import { loadPolkadotCrypto } from "./polkadot-crypto";
// Should try to refacto
const getSendTransactionStatus = (polkadotAPI) => (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const errors = {};
    const warnings = {};
    if (!t.fees) {
        errors.fees = new FeeNotLoaded();
    }
    if (!t.recipient) {
        errors.recipient = new RecipientRequired("");
    }
    else if (a.freshAddress === t.recipient) {
        errors.recipient = new InvalidAddressBecauseDestinationIsAlsoSource();
    }
    else if (!isValidAddress(t.recipient)) {
        errors.recipient = new InvalidAddress("", {
            currencyName: a.currency.name,
        });
    }
    const estimatedFees = t.fees || new BigNumber(0);
    const amount = calculateAmount({
        a,
        t,
    });
    const totalSpent = amount.plus(estimatedFees);
    if (amount.lte(0) && !t.useAllAmount) {
        errors.amount = new AmountRequired();
    }
    const minimumBalanceExistential = getMinimumBalance(a);
    const leftover = a.spendableBalance.minus(totalSpent);
    if (minimumBalanceExistential.gt(0) &&
        leftover.lt(minimumBalanceExistential) &&
        leftover.gt(0)) {
        errors.amount = new PolkadotDoMaxSendInstead("", {
            minimumBalance: formatCurrencyUnit(a.currency.units[0], EXISTENTIAL_DEPOSIT, {
                showCode: true,
            }),
        });
    }
    else if (!errors.amount &&
        !t.useAllAmount &&
        a.spendableBalance.lte(EXISTENTIAL_DEPOSIT.plus(EXISTENTIAL_DEPOSIT_RECOMMENDED_MARGIN))) {
        errors.amount = new NotEnoughBalance();
    }
    else if (totalSpent.gt(a.spendableBalance)) {
        errors.amount = new NotEnoughBalance();
    }
    if (!errors.amount &&
        ((_a = a.polkadotResources) === null || _a === void 0 ? void 0 : _a.lockedBalance.gt(0)) &&
        (t.useAllAmount || a.spendableBalance.minus(totalSpent).lt(FEES_SAFETY_BUFFER))) {
        warnings.amount = new PolkadotAllFundsWarning();
    }
    if (!errors.recipient &&
        amount.lt(EXISTENTIAL_DEPOSIT) &&
        (yield polkadotAPI.isNewAccount(t.recipient))) {
        errors.amount = new NotEnoughBalanceBecauseDestinationNotCreated("", {
            minimalAmount: formatCurrencyUnit(a.currency.units[0], EXISTENTIAL_DEPOSIT, {
                showCode: true,
            }),
        });
    }
    return Promise.resolve({
        errors,
        warnings,
        estimatedFees,
        amount: amount.lt(0) ? new BigNumber(0) : amount,
        totalSpent,
        family: t.family,
    });
});
const getTransactionStatus = (polkadotAPI) => (a, t) => __awaiter(void 0, void 0, void 0, function* () {
    var _b, _c, _d, _e, _f, _g;
    yield loadPolkadotCrypto();
    const errors = {};
    const warnings = {};
    const preloaded = getCurrentPolkadotPreloadData();
    const { staking, validators } = preloaded;
    const minimumBondBalance = new BigNumber(preloaded.minimumBondBalance);
    if (t.mode === "send") {
        return yield getSendTransactionStatus(polkadotAPI)(a, t);
    }
    if ((staking && !staking.electionClosed) || // Preloaded
        (!staking && (yield !polkadotAPI.isElectionClosed())) // Fallback
    ) {
        errors.staking = new PolkadotElectionClosed();
    }
    const amount = calculateAmount({
        a,
        t,
    });
    const unlockingBalance = ((_b = a.polkadotResources) === null || _b === void 0 ? void 0 : _b.unlockingBalance) || new BigNumber(0);
    const unlockedBalance = ((_c = a.polkadotResources) === null || _c === void 0 ? void 0 : _c.unlockedBalance) || new BigNumber(0);
    const currentBonded = ((_d = a.polkadotResources) === null || _d === void 0 ? void 0 : _d.lockedBalance.minus(unlockingBalance)) || new BigNumber(0);
    const minimumAmountToBond = getMinimumAmountToBond(a, minimumBondBalance);
    switch (t.mode) {
        case "bond":
            if (amount.lt(minimumAmountToBond)) {
                errors.amount = new PolkadotBondMinimumAmount("", {
                    minimumBondAmount: formatCurrencyUnit(a.currency.units[0], minimumAmountToBond, {
                        showCode: true,
                    }),
                });
            }
            if (isFirstBond(a)) {
                // Not a stash yet -> bond method sets the controller
                if (!t.recipient) {
                    errors.recipient = new RecipientRequired("");
                }
                else if (!isValidAddress(t.recipient)) {
                    errors.recipient = new InvalidAddress("", {
                        currencyName: a.currency.name,
                    });
                }
                else if (yield polkadotAPI.isControllerAddress(t.recipient)) {
                    errors.recipient = new PolkadotUnauthorizedOperation("Recipient is already a controller");
                }
            }
            break;
        case "unbond":
            if (!isController(a) || !hasLockedBalance(a)) {
                errors.staking = new PolkadotUnauthorizedOperation();
            }
            if (hasMaxUnlockings(a)) {
                errors.unbondings = new PolkadotMaxUnbonding();
            }
            if (amount.lte(0)) {
                errors.amount = new AmountRequired();
            }
            else if (amount.gt(currentBonded.minus(minimumBondBalance)) && amount.lt(currentBonded)) {
                warnings.amount = new PolkadotBondMinimumAmountWarning("", {
                    minimumBondBalance: formatCurrencyUnit(a.currency.units[0], minimumBondBalance, {
                        showCode: true,
                    }),
                });
            }
            else if (amount.gt(currentBonded)) {
                errors.amount = new NotEnoughBalance();
            }
            break;
        case "rebond":
            if (!isController(a)) {
                errors.staking = new PolkadotUnauthorizedOperation();
            }
            if (amount.lte(0)) {
                errors.amount = new AmountRequired();
            }
            else if (amount.gt(unlockingBalance)) {
                errors.amount = new NotEnoughBalance();
            }
            else if (amount.lt(minimumAmountToBond)) {
                warnings.amount = new PolkadotBondMinimumAmountWarning("", {
                    minimumBondBalance: formatCurrencyUnit(a.currency.units[0], minimumBondBalance, {
                        showCode: true,
                    }),
                });
            }
            break;
        case "withdrawUnbonded":
            if (!isController(a)) {
                errors.staking = new PolkadotUnauthorizedOperation();
            }
            if (unlockedBalance.lte(0)) {
                errors.amount = new PolkadotNoUnlockedBalance();
            }
            break;
        case "nominate":
            if (!isController(a)) {
                errors.staking = new PolkadotUnauthorizedOperation();
            }
            else if (!t.validators || ((_e = t.validators) === null || _e === void 0 ? void 0 : _e.length) === 0) {
                errors.staking = new PolkadotValidatorsRequired();
            }
            else {
                if (validators && validators.length) {
                    // Validate directly with preloaded data
                    const notValidators = (_f = t.validators) === null || _f === void 0 ? void 0 : _f.filter(address => !validators.find(v => v.address === address));
                    if (notValidators && notValidators.length) {
                        errors.staking = new PolkadotNotValidator(undefined, {
                            validators: notValidators,
                        });
                    }
                }
                else {
                    // Fallback with api call
                    const notValidators = yield polkadotAPI.verifyValidatorAddresses(t.validators || []);
                    if (notValidators.length) {
                        errors.staking = new PolkadotNotValidator(undefined, {
                            validators: notValidators,
                        });
                        break;
                    }
                }
            }
            break;
        case "chill":
            if (!isController(a)) {
                errors.staking = new PolkadotUnauthorizedOperation();
            }
            else if (!((_g = a.polkadotResources) === null || _g === void 0 ? void 0 : _g.nominations)) {
                errors.staking = new PolkadotNoNominations();
            }
            break;
        case "setController":
            if (!isStash(a)) {
                errors.staking = new PolkadotUnauthorizedOperation();
            }
            break;
    }
    const estimatedFees = t.fees || new BigNumber(0);
    const totalSpent = t.mode === "bond" ? amount.plus(estimatedFees) : estimatedFees;
    if (t.mode === "bond" || t.mode === "unbond" || t.mode === "rebond") {
        if (amount.lte(0)) {
            errors.amount = new AmountRequired();
        }
    }
    if (t.mode === "bond" && a.spendableBalance.minus(totalSpent).lt(FEES_SAFETY_BUFFER)) {
        errors.amount = new NotEnoughBalance();
    }
    if (totalSpent.gt(a.spendableBalance)) {
        errors.amount = new NotEnoughBalance();
    }
    return Promise.resolve({
        errors,
        warnings,
        estimatedFees,
        amount: amount.lt(0) ? new BigNumber(0) : amount,
        totalSpent,
    });
});
export default getTransactionStatus;
//# sourceMappingURL=js-getTransactionStatus.js.map